import { FC, useState } from "react";
import {
  Autocomplete,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { ChallengeType, rootChallengeTypes } from "@/models/pdfData";
import { useSelectedChallengeContext } from "@context/SelectedChallengeContext";
import { getChallengeTypeTranslation } from "@/utils/challenge-utils";
import { useResponsiveContext } from "@context/ResponsiveContext";

interface AutocompleteValue {
  id: ChallengeType;
  label: string;
  invertible: boolean;
}

const ChallengeTypeSelector: FC = () => {
  const { onTablet, isTouchScreen } = useResponsiveContext();
  const {
    challenge: { typeOfChallenge, team },
    teamInterruptedGame,
    setTypeOfChallenge,
  } = useSelectedChallengeContext();
  const [inputValue, setInputValue] = useState(
    typeOfChallenge ? getChallengeTypeTranslation(typeOfChallenge) : "",
  );
  const [value, setValue] = useState<AutocompleteValue | undefined>(() => {
    const ct = rootChallengeTypes.find((c) => c.id === typeOfChallenge);
    return ct
      ? {
          ...ct,
          label: getChallengeTypeTranslation(ct.id),
        }
      : undefined;
  });

  const invertibleChallengesAvailable =
    team !== "referee" && !teamInterruptedGame;

  const challengeTypeOptions = rootChallengeTypes.flatMap((cType) => {
    const toReturn: AutocompleteValue[] = [
      {
        ...cType,
        label: getChallengeTypeTranslation(cType.id),
      },
    ];
    if (cType.invertible && invertibleChallengesAvailable) {
      toReturn.push({
        id: `no-${cType.id}` as ChallengeType,
        label: getChallengeTypeTranslation(`no-${cType.id}` as ChallengeType),
        invertible: true,
      });
    }
    return toReturn;
  });

  return (
    <Card variant="elevation" elevation={5} sx={{ width: "600px", mx: 2 }}>
      <CardHeader
        title={"Challenge Situation"}
        titleTypographyProps={{ variant: "subtitle1", align: "center" }}
        sx={{ pb: 0 }}
      />
      <CardContent>
        {onTablet || isTouchScreen ? (
          <Select
            variant={"outlined"}
            fullWidth
            value={typeOfChallenge || ""}
            onChange={(e: SelectChangeEvent<ChallengeType>) => {
              if (e.target.value) {
                setTypeOfChallenge(e.target.value as ChallengeType);
              }
            }}
          >
            {challengeTypeOptions.map((c, index, types) => (
              <MenuItem
                value={c.id}
                divider={
                  challengeTypeOptions[challengeTypeOptions.length - 1].id !==
                    c.id &&
                  (!invertibleChallengesAvailable ||
                    !c.invertible ||
                    c.id.startsWith("no-"))
                }
                key={c.id}
              >
                {c.label}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Autocomplete
            renderInput={(params) => <TextField {...params} />}
            options={challengeTypeOptions}
            renderOption={(props, c) => {
              const { key, ...optionProps } = props;
              return (
                <MenuItem
                  key={c.id}
                  {...optionProps}
                  value={c.id}
                  divider={
                    challengeTypeOptions[challengeTypeOptions.length - 1].id !==
                      c.id &&
                    (!invertibleChallengesAvailable ||
                      !c.invertible ||
                      c.id.startsWith("no-"))
                  }
                >
                  {c.label}
                </MenuItem>
              );
            }}
            onChange={(event, value) => {
              if (value) {
                setTypeOfChallenge((value.id as ChallengeType) ?? undefined);
                setValue(value);
              }
            }}
            value={value}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ChallengeTypeSelector;
